/* eslint-disable @typescript-eslint/no-explicit-any */
import { UtilityMessageComponent } from '@addToEvent/add-to-event-ui';
import { EmailValidators, TemplateTagValidators } from '@addToEvent/form-controls';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AteModalService } from '@shared/services/ate-modal/ate-modal.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '@environments/environment';
import { EmailHtmlViewerComponent } from '@features/email/components/email-html-viewer.component';
import { AssetUploadFieldComponent } from '@shared/components/asset-upload-field/asset-upload-field.component';
import { Email } from '@features/email/interfaces/email.interface';
import { Enquiry } from '@features/enquiries/interfaces/enquiry.interface';
import { EnquiriesService } from '@features/enquiries/services/enquiries.service';
import { DetailsActions } from '@features/inbox/+state/details';
import {
  SaveTemplateDialogComponent,
  SaveTemplateDialogResult
} from '@features/inbox/modules/details/shared/containers/quote-new/save-template-dialog/save-template-dialog.component';
import { InboxDetailsSharedModule } from '@features/inbox/modules/details/shared/shared.module';
import { TemplatesService as FeaturesTemplatesService } from '@features/templates/services';
import { Listing } from '@marketplace/api';
import { Store } from '@ngrx/store';
import { FormComponentsControlDirective } from '@shared/directives/forms/form-components-wrapper.directive';
import { FlashMessageType } from '@shared/enums/flash-message-type.enum';
import { NavigationStateService } from '@shared/services';
import { AuthService } from '@shared/services/auth/auth.service';
import { EmailSettingsService } from '@shared/services/email/email-settings.service';
import { EnhancedFormsLauncherService } from '@shared/services/enhanced-forms-launcher/enhanced-forms-launcher.service';
import { FlashMessageService } from '@shared/services/flash-message/flash-message.service';
import { GoogleAuthStatus } from '@shared/services/integrations/google/auth/google-auth-status-interface';
import { GoogleAuthService } from '@shared/services/integrations/google/auth/google-auth.service';
import { GmailService } from '@shared/services/integrations/google/gmail/gmail.service';
import { TemplateTokensService } from '@shared/services/template/template-tokens.service';
import { TemplatesService } from '@shared/services/templates/templates.service';
import { SharedModule } from '@shared/shared.module';
import { appQuery } from '@shared/state';
import { arrayBufferToBase64, getMimeTypeFromFilename } from '@shared/utils';
import { format } from 'date-fns';
import { catchError, combineLatest, finalize, first, firstValueFrom, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'email-compose-component',
  templateUrl: './email-compose.component.html',
  styleUrls: ['./email-compose.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    ReactiveFormsModule,
    FormComponentsControlDirective,
    EmailHtmlViewerComponent,
    UtilityMessageComponent,
    InboxDetailsSharedModule,
    AssetUploadFieldComponent
  ],
  providers: [FeaturesTemplatesService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EmailComposeComponent implements OnInit {
  @ViewChild('failedUploadModal') failedUploadModal: ElementRef;

  loading = false;
  form!: FormGroup;
  message: Email | null = null;
  textAreaFocussed = false;
  userUuid = null;
  showingUpload = false;
  fileUploadErrors: string[] = [];
  isConnected = false;
  templateOptions: any[] = [];
  listing: Listing;
  language = 'en';
  isSavingTemplate = false;
  isSendingEmail = false;
  private innerWidth = window.innerWidth;
  private enquiry: Enquiry | null = null;
  private destroyRef = inject(DestroyRef);

  private replyCtx: { threadId?: string; inReplyTo?: string; references?: string } = {};

  private readonly dateFmt = "EEE, dd MMMM yyyy 'at' HH:mm";

  constructor(
    private route: ActivatedRoute,
    private gmail: GmailService,
    private flash: FlashMessageService,
    private _navService: NavigationStateService,
    private router: Router,
    private _authService: AuthService,
    private http: HttpClient,
    private _enhancedFormsLauncherService: EnhancedFormsLauncherService,
    private _googleAuthService: GoogleAuthService,
    private _templatesService: TemplatesService,
    private _featuresTemplatesService: FeaturesTemplatesService,
    private _templateTokensService: TemplateTokensService,
    private _enquiriesService: EnquiriesService,
    private _store: Store,
    private _ateModalService: AteModalService,
    private _flashMessageService: FlashMessageService,
    private _emailSettingsService: EmailSettingsService
  ) {}

  get isReply(): boolean {
    return !!this.message;
  }

  get originalBody(): string {
    const m = this.message;
    return m?.htmlBody ?? m?.textBody ?? '';
  }

  get formattedOriginalDate(): string {
    const ts = Number(this.message?.internalDate);
    if (!ts) return '';
    return format(new Date(ts), this.dateFmt);
  }

  ngOnInit(): void {
    this.buildForm();

    // Get listingUuid from store
    this._store
      .select(appQuery.getListing)
      .pipe(first())
      .subscribe(listing => {
        if (listing) {
          this.listing = listing;
        }
      });

    this.route.paramMap
      .pipe(
        map(pm => pm.get('id')),
        switchMap(id => {
          if (!id) return of(null);
          this.loading = true;
          return this.gmail.getMessage$(id).pipe(
            catchError(err => {
              console.error(err);
              this.flash.show({
                flashMessageType: FlashMessageType.Error,
                message: 'Failed to load original email for reply.'
              });
              return of(null);
            }),
            finalize(() => (this.loading = false))
          );
        })
      )
      .subscribe(msg => {
        if (!msg) return;
        this.message = msg;

        // these are already flattened on your backend
        const messageId = msg.messageId ?? undefined;

        this.replyCtx = {
          threadId: msg.threadId,
          inReplyTo: messageId,
          references: buildReferences(msg, messageId)
        };

        this.form.patchValue({
          to: getReplyAddress(msg),
          subject: ensureRe(msg.subject),
          message: ''
        });
      });

    // Get userUuid for uploads.
    this._authService
      .checkAuthorised$()
      .pipe(first())
      .subscribe(user => {
        this.userUuid = user?.uid;
      });

    // Subscribe for the component lifetime so localStorage stays current whenever
    // Firebase silently refreshes the ID token (roughly every hour).
    this._enhancedFormsLauncherService
      .setUploadAuthToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: err => console.error('Failed to refresh upload auth token', err) });

    const qp = this.route?.snapshot?.queryParamMap;
    if (qp.get('to')) {
      this.form.controls.to.setValue(qp.get('to'));
    }
    if (qp.get('message')) {
      this.form.controls.message.setValue(qp.get('message'));
    }

    const enquiryUuid = qp.get('enquiryUuid');
    const resolvedQuoteUuid = qp.get('quoteUuid');

    if (enquiryUuid) {
      this._enquiriesService
        .getEnquiry(enquiryUuid)
        .pipe(first())
        .subscribe({
          next: enquiry => {
            this.enquiry = enquiry;
          },
          error: err => console.error('Failed to load enquiry for template tokens', err)
        });
    }

    if (resolvedQuoteUuid) {
      this._store.dispatch(DetailsActions.getSelectedQuote({ payload: { uuid: resolvedQuoteUuid } }));
    }

    this._googleAuthService
      .getStatus$({ scope: 'email' })
      .pipe(first())
      .subscribe({
        next: async (googleAuthStatus: GoogleAuthStatus) => {
          this.isConnected = !!googleAuthStatus?.connected;

          if (this.listing) {
            this.populateFromField(googleAuthStatus);
          }
        }
      });
  }

  private populateFromField(googleAuthStatus: GoogleAuthStatus): void {
    const fromValue = this._emailSettingsService.resolveFromString(this.listing, googleAuthStatus);
    this.form.get('from')?.setValue(fromValue);
  }

  goBack(): void {
    this._navService.goBack();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSendingEmail = true;
    this.loading = true;

    const { to, subject, message, files } = this.form.getRawValue();
    const { emailAlias, fromEmail } = this._emailSettingsService.getEmailSettings(this.listing);

    const attachments = await Promise.all(
      files.map(async f => {
        const normalized = this.normalizeFile(f);

        const arrayBuffer = await firstValueFrom(
          this.http.get(`${environment.supplierApiGateway}/sup/file/raw?fileUuid=${normalized.uuid}`, {
            responseType: 'arraybuffer'
          })
        );

        const base64 = arrayBufferToBase64(arrayBuffer);

        return {
          filename: normalized.filename,
          mimeType: normalized.mimeType,
          content: base64
        };
      })
    );

    const finalHtml = this.isReply ? message + this.buildQuotedHtml() : message;

    this.gmail
      .sendMessage$({
        to,
        subject: subject ?? '',
        fromName: emailAlias,
        from: fromEmail,
        html: finalHtml,
        threadId: this.replyCtx.threadId,
        inReplyTo: this.replyCtx.inReplyTo,
        references: this.replyCtx.references,
        attachments
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isSendingEmail = false;
        })
      )
      .subscribe({
        next: () => {
          this.form.reset({ to: '', subject: null, message: '' });
          this.replyCtx = {};
          this.message = null;
          this.flash.show({
            flashMessageType: FlashMessageType.Success,
            message: 'Email sent successfully.'
          });
          this.router.navigate(['/su/me/emails/inbox']);
        },
        error: e => {
          const error = e?.error?.message || 'Failed to send email';
          this.flash.show({ flashMessageType: FlashMessageType.Error, message: error });
        }
      });
  }

  onOutsideTextAreaClick() {
    this.textAreaFocussed = false;
  }

  onTextAreaClick() {
    this.textAreaFocussed = true;
  }

  private buildForm(): void {
    this.form = new FormGroup({
      to: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, EmailValidators.multiEmail]
      }),
      from: new FormControl<string>(
        { value: '', disabled: true },
        {
          nonNullable: true
        }
      ),
      subject: new FormControl<string | null>('', {
        nonNullable: true,
        validators: [Validators.required, TemplateTagValidators.unresolvedTemplateTags]
      }),
      message: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, TemplateTagValidators.unresolvedTemplateTags]
      }),
      files: new FormControl<any[]>([], { nonNullable: true }),
      templateUuid: new FormControl<string>('', { nonNullable: true })
    });

    // Load email templates
    this.loadTemplateOptions();

    // Handle template selection
    this.form.get('templateUuid')?.valueChanges.subscribe(templateUuid => {
      if (!templateUuid) {
        // Clear template content when 'None' is selected
        this.form.patchValue(
          {
            subject: '',
            message: '',
            files: []
          },
          { emitEvent: false }
        );
        this.showingUpload = false;
        return;
      }
      this.loadTemplate(templateUuid);
    });
  }

  /**
   * Fetches the supplier's email templates and refreshes the template dropdown
   * options. Called on init and whenever this tab regains focus, so a template
   * created in the separate "Manage Templates" tab (opened with target="_blank",
   * so it runs in its own app instance and store) appears in the dropdown without
   * a full page reload — which would discard the user's in-progress draft.
   */
  loadTemplateOptions(): void {
    this._templatesService
      .shortTemplatesAsOptions$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: options => {
          this.templateOptions = options;
        },
        error: () => {
          this.templateOptions = [];
        }
      });
  }

  /**
   * When the user returns to this tab (e.g. after creating a template in the
   * "Manage Templates" tab) re-fetch templates so the new one shows immediately.
   */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.loadTemplateOptions();
    }
  }

  loadTemplate(templateUuid: any): void {
    if (!templateUuid) return;
    this._templatesService
      .get$(templateUuid, 'email')
      .pipe(
        switchMap((template: any) => {
          const subject$ = template.subject
            ? this._templateTokensService.replace(
                template.subject
                  .replace(/\\\[/g, '[') // unescape \[ to [
                  .replace(/\\\]/g, ']') // unescape \] to ]
                  .replace(/\\_/g, '_'), // unescape \_ to _
                this.enquiry,
                true,
                false
              )
            : of('');
          const message$ = this._templateTokensService.replace(
            template.message
              .replace(/\\\[/g, '[') // unescape \[ to [
              .replace(/\\\]/g, ']') // unescape \] to ]
              .replace(/\\_/g, '_'), // unescape \_ to _
            this.enquiry,
            true,
            false
          );
          return combineLatest([subject$, message$]).pipe(map(([subject, message]) => ({ subject, message, template })));
        })
      )
      .subscribe({
        next: result => {
          const files = [...(result.template.attachments || []), ...(result.template.images || [])];
          this.form.patchValue(
            {
              subject: result.subject || '',
              message: result.message || '',
              files: files
            },
            { emitEvent: false }
          );

          if (files.length > 0) {
            this.showingUpload = true;
          }
        },
        error: error => {
          console.error('Error loading template:', error);
          this._flashMessageService.show({
            flashMessageType: FlashMessageType.Error,
            message: 'Failed to load template'
          });
        }
      });
  }

  private buildQuotedHtml(): string {
    if (!this.message) return '';

    const fromName = this.message?.from?.name ?? '';
    const fromEmail = this.message?.from?.email ?? '';
    const header = `On ${this.formattedOriginalDate}, ${fromName} &lt;${fromEmail}&gt; wrote:`;

    const original = this.originalBody || '';

    return `
      <div>${header}</div>
      <blockquote style="border-left:1px solid #ccc; margin:0; padding-left:1em;">
        ${original}
      </blockquote>
    `;
  }

  /**
   * Handler for errorEmitter event of upload elements.
   */
  onFileUploadError(e: { detail: string[] }) {
    this.fileUploadErrors = e.detail;
    this.failedUploadModal?.nativeElement?.open();
  }

  /**
   * Handler for click event of the Save as Template button.
   */
  onTemplateSaveClick() {
    if (!this.canSaveTemplate()) {
      return;
    }

    this._ateModalService
      .open<SaveTemplateDialogComponent, SaveTemplateDialogResult>(SaveTemplateDialogComponent, {
        title: 'Save as template',
        hasCta: true,
        hasCancel: true,
        ctaText: 'Save'
      })
      .afterClosed()
      .pipe(first())
      .subscribe((templateName: SaveTemplateDialogResult) => {
        if (!templateName) {
          return;
        }

        this.isSavingTemplate = true;
        const { subject, message, files } = this.form.getRawValue();

        const templateData = {
          title: templateName,
          subject: subject || '',
          message: message || '',
          attachments: files.filter((f: any) => !this.isImageFile(f.filename || f.caption)).map(f => f.uuid),
          images: files.filter((f: any) => this.isImageFile(f.filename || f.caption)).map(f => f.uuid)
        };

        this._featuresTemplatesService
          .createTemplate$(templateData, 'email')
          .pipe(
            first(),
            finalize(() => (this.isSavingTemplate = false))
          )
          .subscribe({
            next: (template: any) => {
              this.flash.show({
                flashMessageType: FlashMessageType.Success,
                message: `Template "${template.title}" was created successfully`
              });
            },
            error: err => {
              this.flash.show({
                flashMessageType: FlashMessageType.Error,
                message: 'Failed to create template. Please try again.'
              });
              console.error('Error creating template:', err);
            }
          });
      });
  }

  /**
   * Check if template can be saved (exposed for template binding)
   */
  canSaveTemplate(): boolean {
    const { subject, message } = this.form.getRawValue();
    return !!(subject?.trim() || message?.trim());
  }

  private isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  }

  private normalizeFile(f: any): { filename: string; mimeType: string; uuid: string } {
    const filename = f.caption || f.filename || '';
    return {
      filename: filename,
      mimeType: f.fileType || f.mimeType || f.type || getMimeTypeFromFilename(filename),
      uuid: f.uuid || ''
    };
  }
}

function ensureRe(subject: string | null | undefined): string {
  if (!subject) return 'Re:';
  return /^re:/i.test(subject) ? subject : `Re: ${subject}`;
}

function getReplyAddress(msg: any): string {
  if (msg.replyTo?.email) return msg.replyTo.email;
  if (typeof msg.replyTo === 'string') return extractEmail(msg.replyTo);
  if (msg.from?.email) return msg.from.email;
  if (typeof msg.from === 'string') return extractEmail(msg.from);
  return '';
}

function buildReferences(msg: any, messageId?: string): string | undefined {
  const refs = msg.references
    ? msg.references
        .split(/\s+/)
        .map((s: string) => s.trim())
        .filter(Boolean)
    : [];

  if (messageId && !refs.includes(messageId)) refs.push(messageId);
  return refs.length ? refs.join(' ') : undefined;
}

function extractEmail(v: string): string {
  const m = v.match(/<([^>]+)>/);
  return (m ? m[1] : v).trim();
}
